import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { Button, Divider, Heading1, Heading2, Paragraph1, Paragraph2 } from 'inpock-ui-react';
import { useQueryClient } from '@tanstack/react-query';

import { PostCartsOrderlinePayload } from '@/apis/services/cart';
import { BottomFixedSheet, Box, OptionSelector, CartItem } from '@/components';
import { campaignOptionSelectSheet, visibleForOptionSelectSheet } from '@/store';
import { Option, Campaign, SubOption, instance, SHIPPING_TYPE, api } from '@/apis';
import { useToast, useDialog, useOptionSelectSheet } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/hooks/queries/keys';
import { CART_TEMPLATE, NON_USER_TEMPLATE, generateStorage } from '@/utils';

export interface TempCartData {
  campaign: Campaign;
  stock_quantity: number;
  option?: Option;
  sub_option?: SubOption;
  additional_product?: {
    id: number;
    additional_product_name: string;
    option_name: string;
    price: number;
    stock_quantity: number | null;
  };
}

interface OptionSelectSheetProps {
  seller?: string;
  marketId?: number;
  disabled?: {
    value: boolean;
    message: string;
  };
  version?: string;
}

export const 최대구매가능수량 = (cartItem: TempCartData): number => {
  const MAX = 999;

  const getStockQuantity = (stockQuantity: number | null) => {
    if (stockQuantity === null) {
      return MAX;
    }
    return stockQuantity;
  };

  if (cartItem.additional_product?.stock_quantity !== undefined) {
    return getStockQuantity(cartItem.additional_product.stock_quantity);
  }

  if (cartItem.sub_option?.stock_quantity !== undefined) {
    return getStockQuantity(cartItem.sub_option.stock_quantity);
  }

  if (cartItem.option?.stock_quantity !== undefined) {
    return getStockQuantity(cartItem.option.stock_quantity);
  }

  return getStockQuantity(cartItem.campaign.stock_quantity);
};

export function OptionSelectSheet({ seller, marketId, disabled, version }: OptionSelectSheetProps) {
  const queryClient = useQueryClient();
  const toastOn = useToast();
  const [dialogOn, dialogOff] = useDialog();
  const safeLocalStorage = generateStorage();

  const { REACT_APP_LINK_CLIENT_URL } = process.env;

  const [isCartLoading, setIsCartLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isCart, setIsCart] = useState<boolean>(false);
  const [tempCartItems, setTempCartItems] = useState<TempCartData[]>([]);
  const isOnlyAdditionalProduct =
    tempCartItems.length > 0 && tempCartItems.every((cart) => cart.additional_product);

  const campaign = useRecoilValue(campaignOptionSelectSheet);
  const visible = useRecoilValue(visibleForOptionSelectSheet);

  const location = useLocation();

  const loc = new URLSearchParams(location.search);
  const preview = loc.get('preview');

  const navigate = useNavigate();
  const { close } = useOptionSelectSheet();

  const { loggedIn, token } = useAuth();

  const pushCart = (newCartItem: TempCartData) => {
    const duplicated = tempCartItems.find((cartItem) => {
      const { campaign, option, sub_option, additional_product } = cartItem;
      if (additional_product) {
        return (
          campaign.id === newCartItem.campaign.id &&
          additional_product.id === newCartItem.additional_product?.id
        );
      }

      // 옵션이 없는 상품인 경우 캠페인 이름만 비교
      if (!option) {
        return campaign.id === newCartItem.campaign.id && !newCartItem.additional_product;
      }

      const { campaign: c, option: o, sub_option: s } = newCartItem;
      return campaign.id === c.id && option?.id === o?.id && sub_option?.id === s?.id;
    });

    if (duplicated) {
      const index = tempCartItems.findIndex((el) => el === duplicated);
      const copiedTempCartItems: TempCartData[] = JSON.parse(JSON.stringify(tempCartItems));
      copiedTempCartItems[index].stock_quantity = copiedTempCartItems[index].stock_quantity + 1;

      if (최대구매가능수량(newCartItem) < copiedTempCartItems[index].stock_quantity) {
        setIsCart(true);
        return toastOn('최대 구매가능수량에 도달했습니다 👏🏻');
      }

      setIsCart(true);
      setTempCartItems(copiedTempCartItems);
      return;
    }

    setIsCart(true);
    setTempCartItems([...tempCartItems, newCartItem]);
  };

  const cartInfo = useMemo(() => {
    const initialPrice = 0;

    const cartItemsPrice = tempCartItems.reduce((prev, curr) => {
      const campaignPrice = curr.campaign.price || 0;
      const optionPrice = curr.option?.price || 0;
      const subOptionPrice = curr.sub_option?.price || 0;
      const additionalProductPrice = curr.additional_product?.price || 0;

      if (curr.additional_product) {
        return prev + curr.stock_quantity * additionalProductPrice;
      }
      if (optionPrice || subOptionPrice) {
        return prev + curr.stock_quantity * (optionPrice + subOptionPrice);
      } else {
        return prev + curr.stock_quantity * campaignPrice;
      }
    }, initialPrice);

    const initialCount = 0;
    const cartItemCount = tempCartItems.reduce(
      (prev, curr) => prev + curr.stock_quantity,
      initialCount,
    );

    return {
      totalPrice: cartItemsPrice,
      totalCount: cartItemCount,
    };
  }, [tempCartItems]);

  // 비회원 유저의 경우 로그인/비회원 구매 선택 다이얼로그
  const openNonUserDialog = (
    orderlines: {
      main_option_id?: number;
      sub_option_id?: number;
      additional_product_id?: number;
      quantity: number;
    }[],
    onClick: () => void,
  ) =>
    dialogOn({
      template: NON_USER_TEMPLATE.signup(onClick), // 비회원으로 구매할게요
      close: {
        text: '닫기',
        onClick: () => {
          dialogOff();
          setIsLoading(false);
        },
      },
      confirm: {
        text: '로그인',
        type: 'danger',
        onClick: () => {
          setIsLoading(false);

          safeLocalStorage.set(
            'checkoutInfo',
            JSON.stringify({
              seller,
              market_id: marketId,
              order_data: [
                {
                  campaign_id: campaign.id,
                  orderlines,
                },
              ],
            }),
          );

          window.location.href = version
            ? `${REACT_APP_LINK_CLIENT_URL}/customer/login?redirect=${encodeURIComponent(
                `/checkout?version=${version}&payment=true`,
              )}`
            : `${REACT_APP_LINK_CLIENT_URL}/customer/login?redirect=${encodeURIComponent(
                '/checkout?payment=true',
              )}`;
        },
      },
    });

  // 장바구니 버튼 클릭 시 먼저 다른 마켓의 상품이 장바구니에 담겨있는지 확인
  const checkOtherCart = async () => {
    try {
      const { has_other_cart } = await api.cart.checkOtherCart(marketId as number);
      if (has_other_cart) {
        dialogOn({
          template: CART_TEMPLATE.add.same_market(),
          close: {
            text: '취소',
            onClick: () => dialogOff(),
          },
          confirm: {
            text: '담기',
            type: 'primary',
            onClick: async () => {
              await addToCart();
              dialogOff();
            },
          },
        });
      } else {
        await addToCart();
      }
    } catch (error) {
      toastOn('장바구니 추가 중 문제가 발생했어요 😢', 2000);
    }
  };

  // 장바구니 버튼 클릭
  const addToCart = async () => {
    const selectedOrderLines = tempCartItems.map((item) => ({
      campaign_id: item.campaign.id,
      main_option_id: item.option?.id,
      sub_option_id: item.sub_option?.id,
      additional_product_id: item.additional_product?.id,
      quantity: item.stock_quantity,
    })) as PostCartsOrderlinePayload[];
    setIsCartLoading(true);

    try {
      await api.cart.postCarts(marketId as number, selectedOrderLines);
      // 장바구니에 상품 추가 시 query invalidate
      queryClient.invalidateQueries(keys.cart.all);
      close();
      dialogOn({
        template: CART_TEMPLATE.add.success(),
        close: {
          text: '장바구니',
          onClick: () => {
            navigate('/cart');
            dialogOff();
          },
        },
        confirm: {
          text: '계속 쇼핑하기',
          type: 'primary',
        },
      });
    } catch (error) {
      toastOn('장바구니 추가 중 문제가 발생했어요 😢', 2000);
    } finally {
      setIsCartLoading(false);
    }
  };

  // 구매하기
  const onClickBuy = async () => {
    if (preview) return toastOn('미리보기 화면에서는 구매할 수 없어요 😢');
    setIsLoading(true);

    const selectedOrderLines = tempCartItems.map((item) => ({
      main_option_id: item.option?.id,
      sub_option_id: item.sub_option?.id,
      additional_product_id: item.additional_product?.id,
      quantity: item.stock_quantity,
    }));

    const handleRedirectToCheckout = () => {
      const checkoutUrl = version ? `/checkout?version=${version}` : `/checkout`;
      navigate(checkoutUrl, {
        state: {
          seller,
          market_id: marketId,
          shipping_type: SHIPPING_TYPE.GENERAL,
          order_data: [
            {
              campaign_id: campaign.id,
              orderlines: selectedOrderLines,
            },
          ],
        },
      });

      close();
    };

    if (loggedIn) {
      try {
        await instance.post('/token/status', { token });
        setIsLoading(false);
        safeLocalStorage.remove('checkoutInfo');
        return handleRedirectToCheckout();
      } catch (error) {
        return openNonUserDialog(selectedOrderLines, handleRedirectToCheckout);
      }
    } else {
      return openNonUserDialog(selectedOrderLines, handleRedirectToCheckout);
    }
  };

  // 옵션이 없는 상품의 경우 상품 자체를 추가
  useEffect(() => {
    if (!campaign.options.length && visible) {
      pushCart({
        campaign,
        stock_quantity: 1,
      });
    }
  }, [campaign, visible]);

  return (
    <BottomFixedSheet
      open={visible}
      onDismiss={close}
      footer={
        tempCartItems.length && isCart ? (
          <>
            <Divider spacing={10} />
            <Box flex alignItems="flex-end" justifyContent="space-between">
              <Box flex alignItems="flex-end">
                <Paragraph1 fontWeight="regular">총 {cartInfo.totalCount}개 상품 금액</Paragraph1>
              </Box>
              <Box flex alignItems="flex-end">
                <Heading1>{cartInfo.totalPrice.toLocaleString()}</Heading1>
                <Heading2 fontWeight="regular">원</Heading2>
              </Box>
            </Box>

            <Box
              flex
              alignItems="center"
              justifyContent="flex-end"
              sx={{ gap: '4px', paddingTop: '4px', paddingBottom: '40px' }}
            >
              {campaign.discount_info ? (
                <Paragraph2 color="orange900">
                  {campaign.discount_info?.min_price_for_discount.toLocaleString()}원 이상 구매 시{' '}
                  {campaign.discount_info?.discount_price.toLocaleString()}원 할인
                </Paragraph2>
              ) : null}
            </Box>
            <div className="d-flex" style={{ gap: '12px' }}>
              {disabled?.value === false && version === 'v2' && (
                <Button
                  size="large"
                  fullWidth
                  onClick={checkOtherCart}
                  loading={isCartLoading}
                  style={{ flex: 3 }}
                >
                  장바구니
                </Button>
              )}
              <Button
                size="large"
                type="primary"
                fullWidth
                onClick={onClickBuy}
                loading={isLoading}
                disabled={disabled?.value || isOnlyAdditionalProduct}
                style={{ flex: 7 }}
              >
                {disabled?.message || '구매하기'}
              </Button>
            </div>
          </>
        ) : (
          <div /> // BottomSheet가 올라왔을 때 아래 간격을 조금 더 주어 구매자에게 편의성을 제공하기 위한 빈 div, 없으면 BottomSheet가 너무 화면 하단에 위치함
        )
      }
    >
      <Box sx={{ paddingTop: '12px', overflowY: 'auto', maxHeight: '60vh' }}>
        <OptionSelector campaign={campaign} onComplete={pushCart} />
        {tempCartItems.length > 0 && isCart && (
          <div className="pl-16 pr-16">
            <CartItem campaign={campaign} carts={tempCartItems} setCarts={setTempCartItems} />
          </div>
        )}
      </Box>
    </BottomFixedSheet>
  );
}
